import React, { useCallback } from "react";
import {
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import { format, isSameDay, startOfDay, addDays } from "date-fns";
import {
  CalendarSlot,
  Meeting,
} from "../../../types";
import {
  useListOrganisations,
  useListUsers,
  useGetCurrentUser,
  useListPatients,
} from "../../../store";
import AgendaMeetingInfo from "./agenda-meeting-info"
import { EditMeetingModal, MeetingInfoModal } from "../../meeting-management";

interface AgendaViewProps {
  meetingList: CalendarSlot[];
  date: Date;
  visibleWeekDays?: number[];
}

const AgendaView: React.FC<AgendaViewProps> = ({
  meetingList,
  date,
  visibleWeekDays = [1, 2, 3, 4, 5],
}) => {
  const [selectedMeetingId, setSelectedMeetingId] = React.useState<string | null>(null);
  const [editingMeeting, setEditingMeeting] = React.useState<Meeting | null>(null);

  const selectedMeeting = React.useMemo(
    () => meetingList.find((meeting) => meeting.meetingId === selectedMeetingId) ?? null,
    [meetingList, selectedMeetingId],
  );

  // Data fetching for the attendees component - MUST be at top before any conditional returns
  const { data: currentUser, isLoading: userLoading, } = useGetCurrentUser()
  const { data: teams = [], isLoading: teamLoading, } = useListOrganisations()
  const { data: users = [], isLoading: usersLoading } = useListUsers()
  const { data: patients = [], isLoading: patientsLoading } = useListPatients()

  // Group meetings by date for the next 30 days starting from the current date
  const startDate = startOfDay(date);
  const dateRange = Array.from({ length: 30 }, (_, i) => addDays(startDate, i)).filter((day) => {
    return visibleWeekDays.includes(day.getDay());
  });

  const groupedMeetings = dateRange.reduce(
    (groups, currentDate) => {
      const meetingsForDate = meetingList.filter((meeting) =>
        isSameDay(new Date(meeting.startTime), currentDate),
      );

      if (meetingsForDate.length > 0) {
        groups[format(currentDate, "yyyy-MM-dd")] = {
          date: currentDate,
          meetings: meetingsForDate.sort(
            (a, b) =>
              new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
          ),
        };
      }

      return groups;
    },
    {} as Record<string, { date: Date; meetings: CalendarSlot[] }>,
  );

  const sortedDateKeys = Object.keys(groupedMeetings).sort();

  // Handle case where loading is false but data might not be ready yet
  const isLoadingData = userLoading || teamLoading || usersLoading || patientsLoading

  // Create stable callback for setEditOpen
  const handleSelectMeeting = useCallback((id: string) => {
    setSelectedMeetingId(id || null);
  }, []);

  const handleOpenEditModal = useCallback((meeting: Meeting | null) => {
    setEditingMeeting(meeting);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingMeeting(null);
  }, []);

  // Create stable callback for closing modal
  const handleCloseModal = useCallback(() => {
    setSelectedMeetingId(null);
  }, []);

  // Show loading state if still loading or if supporting data isn't ready
  if (isLoadingData) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          mx: "auto",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Loading events...
        </Typography>
      </Stack>
    );
  }

  if (sortedDateKeys.length === 0) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          mx: "auto",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {visibleWeekDays.length === 0 ? "No days selected" : "No events in the selected range"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        mx: "auto",
        paddingX: 2,
        paddingBottom: 2,
      }}
    >
      {sortedDateKeys.map((dateKey) => {
        const { date: groupDate, meetings } = groupedMeetings[dateKey];

        return (
          <Paper
            key={dateKey}
            sx={{
              p: 1,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
              border: "1px solid #DDD",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {/* Left side - Date display */}
              <Stack alignItems="center" pl={1} py={0}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: "uppercase" }}
                >
                  {format(groupDate, "EEE")}
                </Typography>

                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1,
                    fontSize: ".8rem",
                    color: "#fff",
                    backgroundColor: "primary.main",
                    width: 25,
                    height: 25,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {format(groupDate, "d")}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{ fontWeight: 500 }}
                >
                  {format(groupDate, "MMMM")}
                </Typography>
              </Stack>

              {/* Right side - Events stacked vertically */}
              <Stack spacing={1} sx={{ flex: 1 }}>
                {currentUser && meetings.map((meeting) => (
                  <AgendaMeetingInfo
                    key={`${meeting.meetingId}-${meeting.startTime}`}
                    event={meeting}
                    handleSelectMeeting={handleSelectMeeting}
                    teams={teams}
                    users={users}
                    patients={patients}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        );
      })}

      {/* Meeting Info Modal */}
      {selectedMeeting && (
        <MeetingInfoModal
          open={selectedMeeting != null}
          handleClose={handleCloseModal}
          meetingId={selectedMeeting.meetingId}
          onEditMeeting={handleOpenEditModal}
        />
      )}

      {editingMeeting && (
        <EditMeetingModal
          open={editingMeeting != null}
          handleClose={handleCloseEditModal}
          meetingId={editingMeeting.meetingId}
        />
      )}
    </Stack>
  );
}

export default AgendaView;
